/**
 * AnnouncementBanner — floating dismissible banner shown on every
 * reader page. Polls `/api/announcements/active` once per page load
 * (cheap) and re-polls every 60 s so a fresh admin post lands fast.
 *
 * UX:
 *   • Default state: full banner pinned to the top of the content area.
 *   • Tap "—" to MINIMIZE → shrinks to a small pill in the topbar; the
 *     pill expands back when tapped. Per-session via sessionStorage.
 *   • Tap "✕" to DISMISS → POST /api/announcements/{id}/dismiss; never
 *     reappears for this user.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, X, Minus, Maximize2, PartyPopper, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MINIMIZED_KEY = 'sl_announcement_minimized_v1';

const TONE_STYLES = {
  info:       { bg: 'from-sky-600/20 to-indigo-700/20', border: 'border-sky-400/40', icon: Info, iconClass: 'text-sky-300' },
  success:    { bg: 'from-emerald-600/20 to-teal-700/20', border: 'border-emerald-400/40', icon: CheckCircle2, iconClass: 'text-emerald-300' },
  warn:       { bg: 'from-amber-600/25 to-orange-700/25', border: 'border-amber-400/50', icon: AlertTriangle, iconClass: 'text-amber-300' },
  celebrate:  { bg: 'from-fuchsia-600/25 to-purple-700/25', border: 'border-fuchsia-400/40', icon: PartyPopper, iconClass: 'text-fuchsia-300' },
};

const AnnouncementBanner = () => {
  const navigate = useNavigate();
  const [ann, setAnn] = useState(null);
  const [minimized, setMinimized] = useState(() => {
    try { return sessionStorage.getItem(MINIMIZED_KEY) === '1'; } catch { return false; }
  });

  const fetchActive = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/announcements/active`);
      if (!r.ok) return;
      const d = await r.json();
      setAnn(d.announcement || null);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchActive();
    const t = setInterval(fetchActive, 60000);
    return () => clearInterval(t);
  }, [fetchActive]);

  if (!ann) return null;
  const tone = TONE_STYLES[ann.tone] || TONE_STYLES.info;
  const Icon = tone.icon;

  const handleMinimize = () => {
    setMinimized(true);
    try { sessionStorage.setItem(MINIMIZED_KEY, '1'); } catch { /* ignore */ }
    try { navigator.vibrate?.(6); } catch (_) {}
  };
  const handleExpand = () => {
    setMinimized(false);
    try { sessionStorage.removeItem(MINIMIZED_KEY); } catch { /* ignore */ }
    try { navigator.vibrate?.(8); } catch (_) {}
  };
  const handleDismiss = async () => {
    try { navigator.vibrate?.(14); } catch (_) {}
    setAnn(null);
    try {
      await authFetch(
        `${BACKEND_URL}/api/announcements/${ann.announcement_id}/dismiss`,
        { method: 'POST' },
      );
    } catch { /* server idempotency keeps us honest */ }
  };
  const handleCta = () => {
    if (ann.cta_url) {
      if (ann.cta_url.startsWith('http')) {
        window.open(ann.cta_url, '_blank', 'noopener');
      } else {
        navigate(ann.cta_url);
      }
    }
  };

  // Minimised pill — small, top-right of content area.
  if (minimized) {
    return (
      <button
        type="button"
        onClick={handleExpand}
        className={`fixed top-16 right-4 z-40 flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${tone.bg} backdrop-blur-md border ${tone.border} hover:scale-105 active:scale-95 transition-transform shadow-lg shadow-black/30`}
        data-testid="announcement-pill"
        aria-label="Open announcement"
        title="Open announcement"
      >
        <Megaphone className={`w-4 h-4 ${tone.iconClass}`} />
        <span className="text-xs font-bold text-white truncate max-w-[160px]">
          {ann.title}
        </span>
        <Maximize2 className="w-3 h-3 text-white/70" />
      </button>
    );
  }

  // Full banner
  return (
    <div
      className={`relative w-full rounded-2xl border ${tone.border} bg-gradient-to-r ${tone.bg} backdrop-blur-md p-4 sm:p-5 mb-4 shadow-xl shadow-black/30 overflow-hidden`}
      data-testid="announcement-banner"
      role="status"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`shrink-0 w-10 h-10 rounded-full bg-white/10 border ${tone.border} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${tone.iconClass}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-bold text-white">{ann.title}</h3>
          <p className="text-xs sm:text-sm text-gray-200 mt-1 whitespace-pre-line">
            {ann.body}
          </p>
          {ann.cta_label && ann.cta_url && (
            <button
              type="button"
              onClick={handleCta}
              className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 active:bg-white/35 border border-white/20 text-white text-xs font-bold transition-colors"
              data-testid="announcement-cta"
            >
              {ann.cta_label} →
            </button>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            type="button"
            onClick={handleMinimize}
            className="w-8 h-8 rounded-full hover:bg-white/15 active:bg-white/25 flex items-center justify-center text-white/80 hover:text-white transition-colors"
            data-testid="announcement-minimize"
            aria-label="Minimize"
            title="Minimize"
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="w-8 h-8 rounded-full hover:bg-white/15 active:bg-red-500/30 flex items-center justify-center text-white/80 hover:text-white transition-colors"
            data-testid="announcement-dismiss"
            aria-label="Dismiss permanently"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;
